import * as logger from 'loglevel';
import { getUuid } from '@front10/helpers/dist/uuid';
import dayjs from 'dayjs';
import axiosWrapper from '../../helpers/axiosWrapper';
import { NO_DURATION_DEFINED } from '../../helpers/axiosWrapper/cache';
import { buildQuery } from './utils/utils';
/**
 * Makes sure the fares response is complain with a basic schema
 * @param {*} fares
 */
const validateFaresResponse = (fares) => {
  const validation = { isValid: true, message: '' };
  if (!fares || !Array.isArray(fares.sale_items)) {
    validation.isValid = false;
    return {
      isValid: false,
      data: fares,
      message: 'INTEGRATION__FARES: SpecialOffers must be an Array'
    };
  }
  if (fares.sale_items.length > 0) {
    fares.sale_items.forEach((fareItem) => {
      const { travel_periods } = fareItem;
      if (
        !fareItem.origin ||
        !fareItem.destination ||
        !fareItem.sale_end ||
        !fareItem.sale_start ||
        !fareItem.from_price ||
        !Array.isArray(travel_periods) ||
        travel_periods.length === 0
      ) {
        logger.warn(
          'INTEGRATION__FARES: Special Offers SCHEMA is invalid, please check API response.',
          fareItem
        );
      }
    });
  }
  return validation;
};

/**
 * The group-routes API does not returns the journeyType
 * so this map allows us to use query sent parameters to do it
 */
const journeyTypeMap = {
  'Round Trip': 'rt',
  'One Way': 'ow',
  'Multi City': 'mc',
  'Each Way': 'ew'
};

function compare(a, b, sorting = { usdTotalPrice: 'ASC' }) {
  const { usdTotalPrice } = sorting;

  const a_price = a.price_sort ?? a.from_price;
  const b_price = b.price_sort ?? b.from_price;

  if (a_price > b_price) {
    return usdTotalPrice === 'ASC' ? 1 : -1;
  }
  if (b_price > a_price) {
    return usdTotalPrice === 'ASC' ? -1 : 1;
  }
  return 0;
}

/**
 * The group routes API returns the data in non linear format
 * so this function will intend normalize the data for the components
 * @param {*} faresRawData
 * @param {*} query
 */
const transformData = (faresRawData, { datePattern, sorting, faresLimit }) => {
  const fares = faresRawData.sale_items
    .sort((a, b) => compare(a, b, sorting))
    .slice(0, faresLimit)
    .filter(({ travel_periods }) => travel_periods.length > 0)
    .map(
      ({
        id,
        cabin,
        origin_display,
        destination_display,
        from_price,
        price_sort,
        dir,
        sale_end,
        sale_start,
        travel_periods,
        ...fare
      }) => {
        const { start_date, end_date, fare_brand } = travel_periods[0];
        const newFare = {
          ...fare,
          index: getUuid(),
          farenetTravelClass: cabin,
          originCity: origin_display,
          destinationCity: destination_display,
          totalPrice: price_sort ?? from_price,
          usdTotalPrice: price_sort ?? from_price,
          formattedTotalPrice: `${price_sort ?? from_price}`,
          formattedUsdTotalPrice: `${price_sort ?? from_price}`,
          journeyType: journeyTypeMap[dir],
          saleStart: dayjs.unix(sale_start).format('YYYY-MM-DD HH:mm:ss'),
          saleEnd: dayjs.unix(sale_end).format('YYYY-MM-DD HH:mm:ss'),
          formattedSaleStart: dayjs.unix(sale_start).format(datePattern),
          formattedSaleEnd: dayjs.unix(sale_end).format(datePattern),
          departureDate: dayjs.unix(start_date).format('YYYY-MM-DD'),
          returnDate: 'n/a',
          travelClass: fare_brand,
          // The API does not return the date formatted in a
          // specific language, the inconvenience of formatting
          // in integration is that it will always take English
          // as locale, possible future path.
          formattedDepartureDate: dayjs.unix(start_date).format(datePattern),
          formattedReturnDate: dayjs.unix(end_date).format(datePattern)
        };
        delete newFare.type;
        delete newFare.id;
        return newFare;
      }
    )
    .reduce((a, b) => a.concat(b), []); // arr.flat()
  return fares;
};

/**
 * Retrieve fares from the GroupRoutes Endpoint
 * @param {*} filter
 * @param {*} integrationSettings
 */
const getFaresSpecialOffers = (filter, integrationSettings, _, meta, pageContext) => {
  return new Promise((resolve, reject) => {
    if (!integrationSettings) {
      const MESSAGE = `INTEGRATION__FARES: **integrationSettings** not specified.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }
    const { v1SpecialOffers: faresApiSettings } = integrationSettings;
    const { cache = false, cacheDuration = NO_DURATION_DEFINED } = faresApiSettings;
    if (
      !faresApiSettings ||
      !faresApiSettings.method ||
      !faresApiSettings.endPoint ||
      !faresApiSettings.auth
    ) {
      const MESSAGE = `INTEGRATION__FARES: **integrationSettings.specialOffers** configuration issues.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }

    const params = buildQuery(filter);
    axiosWrapper(
      {
        method: faresApiSettings.method,
        url: `${faresApiSettings.endPoint}?auth=${faresApiSettings.auth}${params}`,
        headers: { ...faresApiSettings.headers }
      },
      {
        resource: 'INTEGRATION__FARES__SPECIAL__OFFERS',
        meta,
        integrationSettings,
        pageContext,
        cache,
        cacheDuration
      }
    )
      .then((resp) => {
        const validation = validateFaresResponse(resp.data);
        if (validation.isValid) {
          logger.info('INTEGRATION__FARES: Successful request', resp.data);
          const fares = transformData(resp.data, filter);
          resolve(fares);
        } else {
          logger.error(validation.message, validation.data);
          reject(resp.data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export { getFaresSpecialOffers };
